import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "../../../../axios-order";
import { useHistory } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import SelectField from "../../../../components/ui/basicSelectInput";
import { Snackbar, Alert } from "@mui/material";

// ── Design tokens (identical palette to ItcAvailReport) ──────────────────────
const C = {
    navy: "#1a3f6f",
    accent: "#1a6fc4",
    accentLight: "#e8f1fb",
    white: "#ffffff",
    surface: "#f7f9fc",
    border: "#dde3ec",
    borderLight: "#eef1f6",
    text: "#1a3f6f",
    textSub: "#4a6080",
    textMuted: "#8298b2",
    success: { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" },
    warning: { bg: "#fff8e6", text: "#7a4e00", dot: "#f0a500" },
};

const months = [
    { value: "April", label: "April" },
    { value: "May", label: "May" },
    { value: "June", label: "June" },
    { value: "July", label: "July" },
    { value: "August", label: "August" },
    { value: "September", label: "September" },
    { value: "October", label: "October" },
    { value: "November", label: "November" },
    { value: "December", label: "December" },
    { value: "January", label: "January" },
    { value: "February", label: "February" },
    { value: "March", label: "March" },
];

function formatDateTime(dt) {
    if (!dt) return "—";
    const cleaned = dt.replace("Z", "");
    const parts = cleaned.includes("T") ? cleaned.split("T") : cleaned.split(" ");
    const date = parts[0];
    const time = parts[1] || "";
    return (
        <span>
            <span style={{ color: C.text, fontWeight: "500" }}>{date}</span>
            {time && <span style={{ color: C.textMuted }}> · {time}</span>}
        </span>
    );
}

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
function HsnSummaryReport() {
    const [triggerButton] = useState(true);
    const [triggerModelFromToDate, setTriggerModelFromToDate] = useState(false);
    const [selectedFromMonth, setSelectedFromMonth] = useState("");
    const [yearFromArr, setFromYearArr] = useState([]);
    const [selectedToMonth, setSelectedToMonth] = useState("");
    const [selectedReportType, setSelectedReportType] = useState("");
    const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
    const [tdsReconHistoryData, setTdsReconHistoryData] = useState([]);
    const [loaderForTable, setLoaderForTable] = useState(false);
    const [filteredToMonths, setFilteredToMonths] = useState(months);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [branchList, setBranchList] = useState([]);
    const [selectedMainBranch, setSelectedMainBranch] = useState("");
    const [isSelectFieldMandatory, setIsSelectFieldMandatory] = useState(false);
    const [entityType, setEntityType] = useState("");
    const [branchData, setBranchData] = useState([]);
    const [hoveredRow, setHoveredRow] = useState(null);

    const history = useHistory();

    const handleOpenAndCloseModel = () => {
        setTriggerModelFromToDate(false);
    };

    const handleTDSREconciliationButtonEvent = () => setTriggerModelFromToDate(true);
    const handleBack = () => history.push("/reportCards");

    // ── Init ──────────────────────────────────────────────────────────────────
    useEffect(() => { setFromYearValues(); }, []);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        const entity_type = localStorage.getItem("entity_type");
        setEntityType(entity_type);
        axios
            .get(`/multimonth_get_api/${business_entity_id}`)
            .then((res) => {
                const bd = res.data;
                setBranchData(bd);
                const entityChoices = bd.map((entity) => ({
                    value: entity.gstin,
                    label: entity.entity_name,
                }));
                setBranchList(entityChoices);
                setIsSelectFieldMandatory(entity_type === "Group");
                if (entity_type === "Main") setSelectedMainBranch(bd[0].gstin);
            })
            .catch((error) => console.log(error));
    }, []);

    const setFromYearValues = () => {
        let current_year = new Date().getFullYear();
        let yearArr = [];
        for (let i = current_year - 10; i <= current_year + 10; i++) {
            let endYearShort = (i + 1).toString().slice(-2);
            yearArr.push({ id: i, year: `${i}-${endYearShort}` });
        }
        setFromYearArr(yearArr);
    };

    const fetchData = async () => {
        setLoaderForTable(true);
        const business_entity_id = localStorage.getItem("business_entity_id");
        try {
            const response = await axios.get(`/hsn_summary_get_api/${business_entity_id}`);
            setTdsReconHistoryData(response.data.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoaderForTable(false);
        }
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmitHandler = (isReport) => {
        let selectedEntity;
        if (entityType === "Main" || branchData.length === 1) {
            selectedEntity = branchData[0];
        } else {
            selectedEntity = branchData.find((entity) => entity.gstin === selectedMainBranch);
        }

        const business_entity_id = localStorage.getItem("business_entity_id");
        const orgGstin = entityType === "Main" ? branchData[0]?.gstin : selectedEntity?.gstin;

        const payLoadData = {
            org_gstin: orgGstin,
            from_month: selectedFromMonth,
            to_month: selectedToMonth,
            financial_year: selectedFinancialYear,
            business_entity_id,
            is_report: isReport,
            selectedEntity: selectedEntity
                ? selectedEntity.business_entity_id.toString()
                : branchData[0]?.business_entity_id.toString(),
        };

        axios
            .post("/hsn_summary_report", payLoadData)
            .then((res) => {
                if (!res.data.status) {
                    // handle non-success
                } else if (isReport === "yes") {
                    // downloadBase64File(...)
                }
                handleOpenAndCloseModel();
                fetchData();
            })
            .catch((error) => {
                setSnackbarMessage(error.response.data.message);
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
            });
    };

    // ── Navigate to detail ────────────────────────────────────────────────────
    const updatedShowDetails = (item) => {
        history.push({ pathname: `/HsnSummaryDetails/`, state: { items: item } });
    };

    const handleFromMonthChange = (e) => {
        const selectedFrom = e.target.value;
        setSelectedFromMonth(selectedFrom);
        const fromMonthIndex = months.findIndex((month) => month.value === selectedFrom);
        setFilteredToMonths(months.slice(fromMonthIndex));
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        padding: "11px 18px",
        textAlign: "left",
        fontSize: "11px",
        fontWeight: "600",
        letterSpacing: "0.05em",
        color: "#ffffff",
        background: C.navy,
        borderBottom: "1px solid #16355c",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
    };

    const tdStyle = {
        padding: "8px 18px",
        fontSize: "13px",
        color: C.text,
        borderBottom: `1px solid ${C.borderLight}`,
    };

    // ── Status badge helper ───────────────────────────────────────────────────
    const StatusBadge = ({ status }) => {
        const isSuccess = status && (status.includes("Completed") || status.includes("Generated"));
        const scheme = isSuccess ? C.success : C.warning;
        return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", background: scheme.bg, color: scheme.text }}>
                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: scheme.dot, flexShrink: 0 }} />
                {status || "—"}
            </span>
        );
    };

    // ── Table body renderer ───────────────────────────────────────────────────
    const renderTableBody = () => {
        if (loaderForTable) {
            return Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                    {[15, 15, 15, 25, 15, 10].map((w, j) => (
                        <td key={j} style={tdStyle}>
                            <div style={{ height: "14px", borderRadius: "4px", background: C.borderLight, width: `${w}%`, animation: "pulse 1.4s ease-in-out infinite" }} />
                        </td>
                    ))}
                </tr>
            ));
        }

        if (!Array.isArray(tdsReconHistoryData) || tdsReconHistoryData.length === 0) {
            return (
                <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: "center", padding: "52px 20px" }}>
                        <div style={{ fontSize: "28px", marginBottom: "10px", opacity: 0.4 }}>📄</div>
                        <div style={{ fontWeight: "500", marginBottom: "4px", color: C.textSub }}>No reports found</div>
                        <div style={{ fontSize: "12px", color: C.textMuted }}>Generate your first HSN Summary report to get started.</div>
                    </td>
                </tr>
            );
        }

        return tdsReconHistoryData.map((item, i) => (
            <tr
                key={i}
                onMouseEnter={() => setHoveredRow(i)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ background: hoveredRow === i ? C.surface : C.white, transition: "background 0.1s" }}
            >
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.financial_year}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.from_month}</span></td>
                <td style={tdStyle}><span style={{ fontWeight: "600" }}>{item.to_month}</span></td>
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {formatDateTime(item.run_date_time)}
                    </span>
                </td>
                <td style={tdStyle}><StatusBadge status={item.report_status} /></td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                        onClick={() => updatedShowDetails(item.id)}
                        style={{ padding: "5px 14px", borderRadius: "6px", whiteSpace: "nowrap", border: `1px solid ${hoveredRow === i ? C.accent : C.border}`, background: hoveredRow === i ? C.accentLight : C.white, color: hoveredRow === i ? C.accent : C.textSub, fontSize: "12px", fontWeight: "500", cursor: "pointer", transition: "all 0.15s" }}
                    >
                        View report →
                    </button>
                </td>
            </tr>
        ));
    };

    const totalCount = Array.isArray(tdsReconHistoryData) ? tdsReconHistoryData.length : 0;
    const generatedCount = Array.isArray(tdsReconHistoryData)
        ? tdsReconHistoryData.filter(r => r.report_status && (r.report_status.includes("Completed") || r.report_status.includes("Generated"))).length
        : 0;
    const fyCount = Array.isArray(tdsReconHistoryData)
        ? new Set(tdsReconHistoryData.map(r => r.financial_year)).size
        : 0;

    // ── Label style for modal ────────────────────────────────────────────────
    const labelStyle = {
        fontSize: "12px",
        fontWeight: "600",
        color: C.textSub,
        marginBottom: "6px",
        display: "block",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    };

    return (
        <React.Fragment>
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

            <div style={{ padding: "28px 32px", background: C.surface, fontFamily: "inherit" }}>

                {/* ── Page header ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(13,33,55,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <button
                            onClick={handleBack}
                            title="Back to Reports"
                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textSub, fontSize: "14px", flexShrink: 0 }}
                        >←</button>
                        <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DocIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "23px", fontWeight: "700", color: C.text, lineHeight: 1.2 }}>HSN Summary Report</div>
                            <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px" }}>HSN-wise summary report records</div>
                        </div>
                    </div>
                    {triggerButton && (
                        <button
                            onClick={handleTDSREconciliationButtonEvent}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
                            New HSN Summary Report
                        </button>
                    )}
                </div>

                {/* ── Metrics cards ── */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "20px" }}>
                    {[
                        { label: "Total Reports", value: totalCount, borderColor: C.accent, badge: totalCount > 0 },
                        { label: "Generated", value: generatedCount, borderColor: "#28a55f", badge: false },
                        { label: "Financial Years", value: fyCount, borderColor: "#8298b2", badge: false },
                    ].map(({ label, value, borderColor, badge }) => (
                        <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", boxShadow: "0 1px 3px rgba(13,33,55,0.05)", borderTop: `3px solid ${borderColor}` }}>
                            <div style={{ fontSize: "26px", fontWeight: "700", color: C.text, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
                            <div style={{ fontSize: "11px", color: C.textMuted, marginTop: "5px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                            {badge && <div style={{ display: "inline-block", marginTop: "8px", fontSize: "10px", padding: "2px 8px", borderRadius: "12px", background: C.success.bg, color: C.success.text, fontWeight: "600" }}>All generated</div>}
                        </div>
                    ))}
                </div> */}

                {/* ── Table card ── */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(13,33,55,0.05)" }}>
                    <div style={{ maxHeight: "480px", overflowY: "auto", overflowX: "hidden" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                            <colgroup>
                                <col style={{ width: "14%" }} />
                                <col style={{ width: "13%" }} />
                                <col style={{ width: "13%" }} />
                                <col style={{ width: "28%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "14%" }} />
                            </colgroup>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={thStyle}>Financial Year</th>
                                    <th style={thStyle}>From Period</th>
                                    <th style={thStyle}>To Period</th>
                                    <th style={thStyle}>Run Date &amp; Time</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableBody()}</tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {!loaderForTable && Array.isArray(tdsReconHistoryData) && tdsReconHistoryData.length > 0 && (
                        <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.borderLight}`, fontSize: "11px", color: C.textMuted, background: C.white }}>
                            Showing {tdsReconHistoryData.length} record{tdsReconHistoryData.length !== 1 ? "s" : ""}
                        </div>
                    )}
                </div>

            </div>

            {/* ── Modal ── */}
            <Modal size="lg" centered show={triggerModelFromToDate} onHide={handleOpenAndCloseModel}>
                <Modal.Header closeButton style={{ background: C.navy, borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600", color: "#ffffff" }}>
                        New HSN Summary Report
                    </Modal.Title>
                </Modal.Header>

                <ValidatorForm onSubmit={() => onSubmitHandler("no")} onError={(errors) => console.log(errors)}>
                    <Modal.Body style={{ padding: "22px 22px 10px" }}>

                        {/* Row 1: Financial Year + From Month + To Month */}
                        <div style={{ display: "grid", gridTemplateColumns: entityType === "Group" ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: "16px", marginBottom: "18px" }}>
                            <div>
                                <label style={labelStyle}>Financial Year <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    // SelectLabel="Financial Year"
                                    errorMsg={["This field is required"]}
                                    choice_id="year"
                                    choice_name="year"
                                    choices={yearFromArr}
                                    name="financial_year"
                                    value={selectedFinancialYear}
                                    textOnchangeFunction={(e) => setSelectedFinancialYear(e.target.value)}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>From Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    validator={["required"]}
                                    // SelectLabel="From Month"
                                    errorMsg={["This field is required"]}
                                    choice_id="label"
                                    choice_name="label"
                                    choices={months}
                                    name="from_month"
                                    value={selectedFromMonth}
                                    textOnchangeFunction={handleFromMonthChange}
                                />
                            </div>
                            <div>
                                <label style={labelStyle}>To Month <span style={{ color: "#e53e3e" }}>*</span></label>
                                <SelectField
                                    // SelectLabel="To Month"
                                    validator={["required"]}
                                    errorMsg={["This field is required"]}
                                    choice_id="label"
                                    choice_name="label"
                                    choices={filteredToMonths}
                                    name="to_month"
                                    value={selectedToMonth}
                                    textOnchangeFunction={(e) => setSelectedToMonth(e.target.value)}
                                />
                            </div>
                            {entityType === "Group" && (
                                <div>
                                    <label style={labelStyle}>Main Branch <span style={{ color: "#e53e3e" }}>*</span></label>
                                    <SelectField
                                        SelectLabel="Main Branch"
                                        validator={isSelectFieldMandatory ? ["required"] : []}
                                        errorMsg={isSelectFieldMandatory ? ["This field is required"] : []}
                                        choice_id="value"
                                        choice_name="label"
                                        choices={[{ value: "" }, ...branchList]}
                                        name="main_branch"
                                        value={selectedMainBranch || ""}
                                        textOnchangeFunction={(e) => setSelectedMainBranch(e.target.value)}
                                    />
                                </div>
                            )}
                        </div>

                    </Modal.Body>

                    <Modal.Footer style={{ padding: "12px 22px", gap: "8px", borderTop: `1px solid ${C.borderLight}` }}>
                        <button
                            type="button"
                            onClick={handleOpenAndCloseModel}
                            style={{ padding: "0 18px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", color: C.textSub, cursor: "pointer" }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            style={{ padding: "0 22px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                        >
                            Generate Report
                        </button>
                    </Modal.Footer>
                </ValidatorForm>
            </Modal>

            {/* ── Snackbar ── */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>

        </React.Fragment>
    );
}

export default HsnSummaryReport;